// OneSignal push via the Median.co (GoNative) JS bridge.
// Completely inert on a normal browser: the existing VAPID web push (lib/push.js) keeps
// working there. Only active inside the Median-wrapped native app.

const isBrowser = typeof window !== "undefined";

function getBridge() {
  if (!isBrowser) return null;
  return window.median || window.gonative || null;
}

// The OneSignal plugin namespace on the Median bridge (only present in a build that has the
// OneSignal native plugin enabled).
function osApi() {
  const b = getBridge();
  return b && b.onesignal ? b.onesignal : null;
}

export function isMedianApp() {
  return Boolean(osApi());
}

function emitInfo(info) {
  if (!isBrowser || !info) return;
  window.dispatchEvent(new CustomEvent("median:onesignal-info", { detail: info }));
}

// Median invokes these globals automatically; they must exist synchronously at page load.
// A push tap can fire BEFORE the app's React listeners attach (cold start) or survive across
// Median's targetUrl navigation — so we buffer the last opened payload (memory + sessionStorage)
// and let the app consume it once it's ready via consumePushOpened().
let pendingOpened = null;
const PUSH_KEY = "rc_push_opened";

function bufferOpened(data) {
  pendingOpened = data || {};
  try { sessionStorage.setItem(PUSH_KEY, JSON.stringify(pendingOpened)); } catch (e) { /* ignore */ }
}

export function consumePushOpened() {
  let d = pendingOpened;
  if (!d) {
    try { const s = sessionStorage.getItem(PUSH_KEY); if (s) d = JSON.parse(s); } catch (e) { /* ignore */ }
  }
  pendingOpened = null;
  try { sessionStorage.removeItem(PUSH_KEY); } catch (e) { /* ignore */ }
  return d;
}

if (isBrowser) {
  window.median_onesignal_info = (info) => emitInfo(info);
  window.median_onesignal_push_opened = (data) => {
    bufferOpened(data);
    window.dispatchEvent(new CustomEvent("median:push-opened", { detail: data || {} }));
  };
  // Some Median builds use a different global for notification action buttons.
  window.median_onesignal_notification_action = (data) => {
    bufferOpened(data);
    window.dispatchEvent(new CustomEvent("median:push-opened", { detail: data || {} }));
  };
}

// Median exposes info() on current builds; older builds use onesignalInfo(). Support both.
async function readInfo() {
  const os = osApi();
  if (!os) return null;
  const fn = os.info || os.onesignalInfo;
  if (typeof fn !== "function") return null;
  try { return await fn.call(os); } catch (e) { return null; }
}

export async function getOneSignalInfo() {
  const info = await readInfo();
  if (info) emitInfo(info);
  return info;
}

export async function loginMedianOneSignal(userId) {
  const os = osApi();
  if (!os || !userId) return null;
  const id = String(userId);
  // Link this device to the RoyalChat Nova user. Cover BOTH Median modes:
  //  - SDK v5 (user-centric): median.onesignal.login(externalId)
  //  - Legacy mode:           median.onesignal.externalUserId.set({ externalId })
  try { if (typeof os.login === "function") await os.login(id); } catch (e) { /* ignore */ }
  try { if (os.externalUserId && typeof os.externalUserId.set === "function") await os.externalUserId.set({ externalId: id }); } catch (e) { /* ignore */ }
  // Some builds expose a permission prompt / register helper — harmless if absent.
  try { if (typeof os.register === "function") await os.register(); } catch (e) { /* ignore */ }
  return getOneSignalInfo();
}

export async function logoutMedianOneSignal() {
  const os = osApi();
  if (typeof os?.logout === "function") {
    try { await os.logout(); } catch (e) { /* ignore */ }
  }
}

// Clear the notification tray + reset the app badge when the user opens/focuses the app.
// Covers the Median/OneSignal native bridge AND the web/PWA service-worker notifications.
export async function clearMedianNotifications() {
  const os = osApi();
  if (os) {
    try { if (typeof os.clearAllNotifications === "function") await os.clearAllNotifications(); } catch (e) { /* ignore */ }
    try { if (os.notifications && typeof os.notifications.clearAll === "function") await os.notifications.clearAll(); } catch (e) { /* ignore */ }
    try { if (os.badgeCount && typeof os.badgeCount.set === "function") await os.badgeCount.set(0); } catch (e) { /* ignore */ }
    try { if (os.badge && typeof os.badge.set === "function") await os.badge.set(0); } catch (e) { /* ignore */ }
  }
}

export async function clearAppNotifications() {
  await clearMedianNotifications();
  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg && reg.getNotifications) {
        const ns = await reg.getNotifications();
        ns.forEach((n) => { try { n.close(); } catch (e) { /* ignore */ } });
      }
    }
  } catch (e) { /* ignore */ }
  try { if (typeof navigator !== "undefined" && navigator.clearAppBadge) await navigator.clearAppBadge(); } catch (e) { /* ignore */ }
}

export function subscribeMedianEvents({ onInfo, onOpened } = {}) {
  if (!isBrowser || !isMedianApp()) return () => {};
  const infoHandler = (e) => onInfo?.(e.detail);
  const openedHandler = (e) => onOpened?.(e.detail);
  window.addEventListener("median:onesignal-info", infoHandler);
  window.addEventListener("median:push-opened", openedHandler);
  return () => {
    window.removeEventListener("median:onesignal-info", infoHandler);
    window.removeEventListener("median:push-opened", openedHandler);
  };
}

export async function getBridgeDiagnostics() {
  const b = getBridge();
  const os = osApi();
  let info = null;
  try { info = await readInfo(); } catch (e) { /* ignore */ }
  return {
    hasMedian: Boolean(b),
    hasOneSignal: Boolean(os),
    methods: os ? Object.keys(os) : [],
    info,
  };
}

// The Median bridge may inject slightly after the page's JS runs. Poll briefly so we don't
// miss the window where median.onesignal becomes available.
export function whenMedianReady(cb, { tries = 20, interval = 300 } = {}) {
  if (!isBrowser) return () => {};
  let n = 0, timer = null, done = false;
  const tick = () => {
    if (done) return;
    if (isMedianApp()) { done = true; cb(); return; }
    if (++n >= tries) { done = true; return; }
    timer = setTimeout(tick, interval);
  };
  tick();
  return () => { done = true; if (timer) clearTimeout(timer); };
}
